/* @flow */
/* eslint-disable no-mixed-operators */
/* eslint-disable space-infix-ops */
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import ReactModal from 'react-modal';
import { t } from 'javascripts/utils/withTranslation';
import { useMediaQuery } from 'javascripts/hooks/useMediaquery';
import { MEDIA_QUERIES } from 'javascripts/constants';
import Button from 'components/atoms/button/Button';
import Select from 'components/atoms/select/react/Select';
import TabBar from 'components/molecules/tab-bar/react/TabBar';
import Teaser from 'components/molecules/teaser/react/Teaser';
import Headline from 'components/atoms/headline/Headline';
import IconLink from 'components/atoms/icon-link/IconLink';
import LinkList from 'components/molecules/link-list/LinkList';
import type { LinkType } from 'components/atoms/link/Link';
import type { IconLinkType } from 'components/atoms/icon-link/IconLink';
import { componentProps } from '../../share-dialog/share-dialog';
import DiagramContainer from './DiagramContainer';
import Map from './Map';
import YearRange from './YearRange';
import { PRESETKEY, ZNCODE } from './types';
import type {
  Data,
  Tab,
  Fakt,
  NumberFormat,
  Time,
} from './types';


const MapInteractiveStates = ({ tabs, defaultFaktrelease }: Data): React$Element<"div"> => {
  const isDesktop = useMediaQuery(MEDIA_QUERIES.headernav);
  const [activeTab, setActiveTab] = useState();
  const [topic, setTopic] = useState();
  const [faktrelease, setFaktrelease] = useState();
  const [state, setState] = useState();
  const [hoverState, setHoverState] = useState();
  // url params can be set to share links,
  // if defaultFaktrelease is set, they will initially be overwritten
  const [urlParams, setUrlParams] = useState(new URLSearchParams(window.location.search));
  const defaultFormat = {
    unit: '',
    decimals: 0,
    decpoint: ',',
  };
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);

  const setUrl = (key: string, value?: string | null) => {
    if (value) {
      const encoded = encodeURI(value);
      urlParams.set(key, encoded);
    } else if (urlParams.has(key)) {
      urlParams.delete(key);
    }
    setUrlParams(urlParams);
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
  };

  const getTimeFromObject = (ti?: Time) => {
    if (ti && ti.year && ti.month) {
      return new Date(ti.year, ti.month - 1).getTime();
    }
    if (ti && ti.year) {
      return ti.year;
    }
    return undefined;
  };

  useEffect(() => {
    if (defaultFaktrelease) {
      tabs.forEach((tab) => {
        tab.topicdatas.forEach((to) => {
          to.faktrelease.forEach((f) => {
            if (f.id === defaultFaktrelease) {
              setActiveTab(tab);
              setTopic(to);
              setUrl(PRESETKEY.FAKTID, defaultFaktrelease.toString());
              setFaktrelease(f);
            }
          });
        });
      });
    }
  }, []);

  useEffect(() => {
    const defaultTab = tabs[0];
    const presetParam = decodeURI(urlParams.get(PRESETKEY.FAKTID) || '');
    const presetTab = tabs.find((tab) => {
      let find;
      tab.topicdatas.forEach((to) => {
        to.faktrelease.forEach((f) => {
          if (presetParam !== '' && f.id === parseInt(presetParam, 10)) {
            find = tab;
            setTopic(to);
            setFaktrelease(f);
          }
        });
      });
      return find;
    });
    setActiveTab(presetTab || defaultTab);
    if (!presetTab) {
      setTopic(defaultTab.topicdatas[0]);
      const latestItem = defaultTab.topicdatas[0].faktrelease.sort((a, b) => (
        getTimeFromObject(b.time) || 0) - (getTimeFromObject(a.time) || 0))[0];
      setFaktrelease(latestItem);
    }
  }, [urlParams]);

  useEffect(() => {
    if (activeTab && !urlParams.has(PRESETKEY.FAKTID)) {
      setTopic(activeTab.topicdatas[0]);
    }
  }, [activeTab]);

  useEffect(() => {
    if (topic) {
      // get latest item of data sorted by time descending
      const latestItem = topic.faktrelease.sort((a, b) => (
        getTimeFromObject(b.time) || 0) - (getTimeFromObject(a.time) || 0))[0];
      let faktReleaseSelected = faktrelease || latestItem;

      if (faktrelease) {
        const faktreleaseIsInTopic = topic.faktrelease.find(f => f.id === faktrelease.id);

        if (faktrelease && !faktreleaseIsInTopic) {
          faktReleaseSelected = latestItem;
        }
      }

      setFaktrelease(faktReleaseSelected);
    }
  }, [topic]);

  useEffect(() => {
    if (faktrelease && faktrelease.fakt) {
      if (urlParams.has(PRESETKEY.COMPARE)) {
        const stateToFind = faktrelease.fakt
          .find(f => f.faktregion.zncode === urlParams.get(PRESETKEY.COMPARE));
        setState(stateToFind);
      } else if (state) {
        // keep selected state at fakt change
        setState(faktrelease.fakt.find(i => i.faktregion.zncode === state.faktregion.zncode));
      }
      setUrl(PRESETKEY.FAKTID, faktrelease.id.toString());
    }
  }, [faktrelease]);

  const handleTabChange = (newTab: Tab) => {
    urlParams.delete(PRESETKEY.FAKTID);
    setActiveTab(newTab);
  };

  const onTopicChange = (event) => {
    const target = event.currentTarget;

    if (target && activeTab) {
      const topicData = activeTab.topicdatas.find(i => i.title === target.value);
      if (topicData) {
        setTopic(topicData);
      }
    }
  };

  const handleTimeChange = (ti: number) => {
    if (topic) {
      const faktToFind = topic.faktrelease.find(fakt => getTimeFromObject(fakt.time) === ti);
      setFaktrelease(faktToFind || topic.faktrelease[0]);
    }
  };

  const handleStateChange = (stateData?: Fakt, stayFocussed?: boolean) => {
    if (stayFocussed) {
      setState(stateData);
      setUrl(PRESETKEY.COMPARE, stateData ? stateData.faktregion.zncode : undefined);
    } else {
      setHoverState(stateData);
    }
  };

  const handleMapStateChange = (location?: $Keys<typeof ZNCODE>, stayFocussed?: boolean) => {
    if (faktrelease && faktrelease.fakt) {
      const stateToFind = location &&
        faktrelease.fakt.find(i => i.faktregion.zncode === location.toUpperCase());
      handleStateChange(stateToFind, stayFocussed);
    }
  };

  const getShareItems = (): Array<IconLinkType | LinkType> => {
    const location = encodeURIComponent(window.location);
    const title = encodeURIComponent(document.title);

    return [
      Object.assign(componentProps.items[0], {
        link: `https://www.facebook.com/sharer/sharer.php?u=${location}`,
      }),
      Object.assign(componentProps.items[1], {
        link: `http://twitter.com/intent/tweet?text=${title} ${location}&via=regierungbw`,
      }),
      Object.assign(componentProps.items[2], {
        link: `https://www.linkedin.com/shareArticle?mini=true&url=${location}&title=${title} &source="Baden-Württemberg"`,
      }),
      Object.assign(componentProps.items[3], {
        link: `https://www.xing.com/app/user?op=share&url=${location}`,
      }),
      Object.assign(componentProps.items[4], {
        link: `mailto:?subject=${title}&body=${location}`,
      }),
    ];
  };

  return (
    <div className='map-is'>
      <div className='page__section page__section--breaker map-is__breaker t-beige'>
        <div className='map-is__breaker-inner'>
          <TabBar
            activeTab={activeTab && activeTab.tabtitle}
            onTabClick={handleTabChange}
            tabs={tabs}
            className='map-is__tabbar'
          />

          <div className='map-is__wrap'>
            {isDesktop &&
              <Map
                activeFakt={faktrelease && faktrelease.fakt}
                activeState={state && state.faktregion.zncode}
                hoverState={hoverState && hoverState.faktregion.zncode}
                onStateChange={handleMapStateChange}
              />
            }

            <div className='map-is__data'>
                {activeTab && <Select
                  onChange={onTopicChange}
                  value={topic && topic.title}
                  options={activeTab.topicdatas.map(to => ({ value: to.title, text: to.title }))}/>}

              {topic && topic.topicexplanation &&
                <div className='map-is__explanation text' dangerouslySetInnerHTML={{ __html: topic.topicexplanation }}/>
              }

              {topic && topic.faktrelease.length > 1 && faktrelease &&
                <YearRange
                  key={`${topic.title}--${getTimeFromObject(faktrelease.time) || '#'}`}
                  value={getTimeFromObject(faktrelease.time)}
                  timeMarks={topic.faktrelease
                    .map(i => getTimeFromObject(i.time))}
                  onChange={handleTimeChange}/>
              }

              {topic &&
                <DiagramContainer
                  data={faktrelease && faktrelease.fakt}
                  activeState={state}
                  hoverState={hoverState}
                  onStateChange={handleStateChange}
                  unitFormat={(topic.format: NumberFormat) || defaultFormat}
                  average={faktrelease && faktrelease.averagevalue}
                  orderascending={topic.orderascending}
                />
              }
            </div>

          </div>

          <div className='map-is__share'>
            <IconLink icon='share' onClick={() => setShareModalIsOpen(true)}>Teilen</IconLink>
          </div>

            <div className='map-is__diagram-sources'>
              {[(faktrelease && faktrelease.source), (state && state.link)].map((i, index) => (
                i && (
                    <div className='map-is__diagram-source' key={`${i.url}--${index}`}>
                      <span className='map-is__diagram-source-label'>{index === 0 ? 'Quelle: ' : 'Link: '}</span>
                      {i.url ?
                        <a href={i.url} target='_blank' rel='noopener noreferrer' className='link'>{i.text}</a> :
                        <span>{i.text}</span>
                      }
                    </div>
                )))
              }
            </div>

        </div>
      </div>

      <div className='page__section map-is__teasers'>
        {activeTab && activeTab.teasersheadline &&
          <Headline size={2} level={2} headlineClass='map-is__headline'>
            {activeTab.teasersheadline}
          </Headline>
        }
        {activeTab && activeTab.teasers && activeTab.teasers.length !== 0 &&
          <div className='grid grid--33-33-33'>
            <div className='grid__row'>
              {activeTab.teasers.map((teaser, i) => (
                <div className={classNames('grid__col', `grid__col--${i}`)} key={`${i}--${activeTab.tabtitle}`}>
                  <div className='grid__item'>
                    <Teaser {...teaser}
                      headline={teaser.title && teaser.title}
                      meta={[{ text: teaser.subtitle && teaser.subtitle }]}
                      moreLink={teaser.link && { link: teaser.link.url, text: teaser.link.text }}
                      copyright={teaser.image && teaser.image.copyright}
                      key={`${i}--${activeTab.tabtitle}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        }
      </div>
      <ReactModal
        isOpen={shareModalIsOpen}
        onRequestClose={() => setShareModalIsOpen(false)}
        role="dialog"
        // parentSelector={() => dialogParent}
        contentLabel="Dialog Overlay"
        portalClassName={classNames(shareModalIsOpen && 'dialog')}
        overlayClassName='dialog__overlay'
        className={classNames('dialog__content', 'share-dialog__content')}
      >
        <div className='dialog__inner'>
          <div className='dialog__scroll-inner'>
            <LinkList items={getShareItems()}/>
          </div>
        </div>
        <Button
          icon="close"
          iconTitle={t('Schließen')}
          className="dialog__close"
          style="chromeless"
          onClick={() => setShareModalIsOpen(false)}
        />
      </ReactModal>
    </div>
  );
};

export default MapInteractiveStates;
